// Buscas dos indicadores que dependem dos registros novos da #6509. Protocolo da titularidade e
// custo de ferramentas dependem da migration 20261029120000: sem ela, a busca volta
// `semMigration: true` e a tela avisa, em vez de quebrar o resto.
import { supabase } from '../../src/lib/supabase';
import type { CancelamentoDoHistorico } from '../../src/lib/motivosCancelamentoBoleto';
import type { PedidoEncerrado } from '../../src/lib/resultadoCancelamento';
import type { Ferramenta, ProtocoloDaTroca } from '../../src/lib/visaoGeral/registrosFase3';
import { paginar, useBusca } from './useTecnologiaMarketing';

/** Coluna ou tabela que ainda não existe (migration não aplicada). */
const faltaMigration = (e: any) => ['42703', '42P01', 'PGRST204', 'PGRST205'].includes(e?.code)
  || /does not exist|could not find/i.test(e?.message || '');

/** Protocolo da troca e da transferência de saldo, por termo de titularidade. */
export const useProtocolosTitularidade = (versao: number) => useBusca<{ porTermo: Map<number, ProtocoloDaTroca>; semMigration: boolean }>(async () => {
  const { data, error } = await supabase.from('termo_troca_titularidade_logs')
    .select('id, protocolo_titularidade, titularidade_protocolada_em, protocolo_saldo, saldo_protocolado_em, saldo_nao_se_aplica');
  if (error) {
    if (faltaMigration(error)) return { porTermo: new Map(), semMigration: true };
    throw error;
  }
  return { porTermo: new Map(((data || []) as ProtocoloDaTroca[]).map(p => [Number(p.id), p])), semMigration: false };
}, [versao], 'Protocolos da titularidade');

/** Ferramentas cadastradas (só adm lê). */
export const useCustosFerramentas = (versao: number) => useBusca<{ ferramentas: Ferramenta[]; semMigration: boolean }>(async () => {
  const { data, error } = await supabase.from('custos_ferramentas')
    .select('id, nome, area, valor_mensal, inicio, fim, observacao').order('nome');
  if (error) {
    if (faltaMigration(error)) return { ferramentas: [], semMigration: true };
    throw error;
  }
  return { ferramentas: (data || []) as Ferramenta[], semMigration: false };
}, [versao], 'Custos de ferramentas');

/** Pedidos de cancelamento encerrados (concluídos ou com desistência) desde `desdeIso`. */
export const usePedidosEncerrados = (desdeIso: string) => useBusca<PedidoEncerrado[]>(() =>
  paginar<PedidoEncerrado>(() => supabase.from('solicitacoes_cancelamento')
    .select('id, status, concluida_em, concluida_motivo, origem')
    .in('status', ['concluida', 'cancelada'])
    .gte('concluida_em', desdeIso)
    .order('id')), [desdeIso], 'Pedidos de cancelamento encerrados');

/**
 * Histórico de cancelamentos das faturas com boleto cancelado desde `desdeIso` (o
 * `cancelado_em` da fatura é o último; o histórico tem todos).
 */
export const useCancelamentosDeBoleto = (desdeIso: string) => useBusca<(CancelamentoDoHistorico[] | null)[]>(async () => {
  const linhas = await paginar<{ historico_cancelamentos: CancelamentoDoHistorico[] | null }>(() => supabase.from('faturas')
    .select('id, historico_cancelamentos')
    .gte('cancelado_em', desdeIso)
    .order('id'));
  return linhas.map(l => l.historico_cancelamentos);
}, [desdeIso], 'Cancelamentos de boleto');
